import React, { useMemo } from "react";
import nr from "newrelic";
import type { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import type { NextParsedUrlQuery } from "next/dist/server/request-meta";
import Head from "next/head";
import { ContainerWithParallaxLeaderboard } from "@bloomberg/media-ui-ads";
import Logger from "@foundation/logger";
import {
    Favicon,
    OpenGraph,
    ParselyMetadata,
    StaticMetadata,
    TwitterMetadata,
} from "@foundation/metadata";
import { REGIONS, NavProvider } from "@foundation/nav";
import { Tophat } from "@foundation/nextjs-tophat";
import { parseGeoInfo } from "@foundation/sparkle/lib/adDials/parseGeoInfo";
import AdOptionsProvider from "@foundation/sparkle/lib/AdOptionsProvider";
import type { Targeting } from "@foundation/sparkle/lib/AdTargeting/TargetingProvider";
import WinSparkleProvider from "@foundation/sparkle/lib/WinSparkleProvider";
import { TrackingProvider } from "@foundation/with-tracking";

import { AD_CODE_MAP, ENVIRONMENTS } from "~constants";
import { getAdConfig, type AdConfig } from "~api/getAdConfig";
import { fetchMagazine } from "~api/magazine";
import MagazinePageMagazineArchive from "~components/modules/magazine-phx/variants/magazine-archive/MagazinePageMagazineArchive";
import TableOfContentsTitle from "~components/modules/magazine-phx/variants/title/TableOfContentsTitle";
import Footer from "~components/shared/footer";
import { GridLayout } from "~components/shared/grid/gridLayout";
import MagazineLede from "~components/shared/magazineLede";
import NavContainer from "~components/shared/nav";
import { gridZonesWithColumns } from "~components/shared/zones/utility";
import gridStyles from "~components/shared/zones/zone.module.css";
import { DESCRIPTION } from "~constants/magazine";
import { PageContext } from "~context/PageContext";
import useRedux from "~redux/hooks";
import {
    ZoneColumnCombo,
    ZoneType,
    ModuleSource,
    type MagazineIssue,
} from "~types/types";
import { getAdTargeting } from "~utils/AdTargetingClient";
import {
    getMenuItems,
    getTophat,
    getFooterMenuItems,
} from "~utils/commonPageResources";
import { handlePageError } from "~utils/errorHandler";
import { getSparkleProviderOptions } from "~utils/getSparkleProviderOptions";
import { getCurrentUrl } from "~utils/nextContextUtils";
import sizeImage from "~utils/sizeImage";

interface MagazinePageProps {
    brand: string;
    id: string;
    magazineIssue: MagazineIssue;
    region?: REGIONS;
    countryCode?: string;
    adTargetings?: Targeting;
    adConfig: AdConfig;
}

export interface MagazinePageParams extends NextParsedUrlQuery {
    brand: string;
    magazineId: string;
}

export interface MagazinePageGetServerSidePropsContext
    extends GetServerSidePropsContext {
    params: MagazinePageParams;
}

type MagazinePageGetServerSideProps<
    P extends MagazinePageProps = MagazinePageProps
> = (
    context: MagazinePageGetServerSidePropsContext
) => Promise<GetServerSidePropsResult<P>>;

const COLOR_ENV = process.env.NEXT_PUBLIC_COLOR_ENV;

export const getServerSideProps:
    | MagazinePageGetServerSideProps
    | unknown = async (context: MagazinePageGetServerSidePropsContext) => {
    const {
        params: { brand, magazineId: id },
        req: { cookies },
        res,
    } = context;
    const countryCode = cookies?.country_code || "US";
    const region = cookies?.exp_pref || REGIONS.US;
    const geoInfo = parseGeoInfo(cookies?.geo_info);

    nr.addCustomAttributes({
        brand,
        id,
    });

    Logger.info(`${id} - Lineup magazine id`);

    let magazine: MagazineIssue;

    try {
        magazine = await fetchMagazine({
            brand,
            magazineId: id,
            inPreview: "false",
        });
    } catch (error) {
        return handlePageError({
            error,
            res,
            newrelicParams: { brand, id },
        });
    }

    const pageId = `${brand}-magazine`;
    const adCode = AD_CODE_MAP[pageId];

    return Promise.all([
        getTophat(),
        getMenuItems(),
        getFooterMenuItems(),
        getAdTargeting({
            pageId,
            adCode,
            adPageType: "other",
            url: getCurrentUrl(context),
        }),
    ])
        .then(([tophatSnippet, menuItems, footerMenuItems, adTargetings]) => {
            return {
                props: {
                    initialState: {
                        tophatSnippet,
                        navMenuItems: {
                            menuItems,
                            footerMenuItems,
                        },
                    },
                    brand,
                    id,
                    countryCode,
                    magazineIssue: magazine,
                    region,
                    adTargetings,
                    adConfig: getAdConfig({
                        ...geoInfo,
                        adCode: adTargetings.adCode as string,
                    }),
                },
            };
        })
        .catch((error) => {
            const errorDetails = {
                message: "Unable to retrieve magazine page data",
                id,
                error,
            };
            Logger.error(errorDetails);
            throw new Error(error);
        });
};

export default function MagazinePhoenixPage({
    brand,
    id,
    magazineIssue,
    region,
    countryCode,
    adTargetings,
    adConfig,
}: MagazinePageProps) {
    const { useAppSelector } = useRedux();
    const [tophatSnippet, menuItems, footerMenuItems] = useAppSelector(
        (state) => [
            state.tophatSnippet,
            state.navMenuItems.menuItems,
            state.navMenuItems.footerMenuItems,
        ]
    );

    const pageId = `${brand}-magazine`;

    const sparkleOptions = React.useMemo(
        () =>
            getSparkleProviderOptions(
                pageId,
                AD_CODE_MAP[pageId],
                undefined,
                adTargetings
            ),
        [pageId, adTargetings]
    );

    const url = `https://bloomberg.com/magazine/${brand}/${id}`;
    const displayDate = magazineIssue.displayDate ?? magazineIssue.date;
    const title = `${magazineIssue.title} - ${displayDate} Issue`;
    const shareImage = magazineIssue.coverImage?.baseUrl
        ? sizeImage(magazineIssue.coverImage?.baseUrl, 1200, 630)
        : "";

    const sectionModules = magazineIssue.modules ?? [];
    const pageContextValue = useMemo(() => ({ useGrid: true }), []);

    const trackingData = {
        count: 0,
        pageviewId: "",
    };

    // TODO: pass in archive link to <TableOfContentsTitle /> once that data is available

    return (
        <div data-page-type="lineup-magazine">
            <Head>
                <title key="title">{title}</title>
                <Favicon brand={brand} env={COLOR_ENV} />
                <StaticMetadata
                    canonical={url}
                    description={DESCRIPTION[brand]}
                />
                <ParselyMetadata
                    title={title}
                    link={url}
                    type="sectionpage"
                    image={shareImage}
                />
                <OpenGraph
                    description={DESCRIPTION[brand]}
                    title={title}
                    type="website"
                    url={url}
                    image={shareImage}
                />
                <TwitterMetadata
                    description={DESCRIPTION[brand]}
                    twitterHandle="@business"
                    title={title}
                    image={shareImage}
                />
            </Head>
            <TrackingProvider addTrackingAnchor trackingData={trackingData}>
                <WinSparkleProvider {...sparkleOptions}>
                    <AdOptionsProvider adOptions={adConfig.globalAdOptions}>
                        <ContainerWithParallaxLeaderboard isDecoupled>
                            <Tophat snippet={tophatSnippet} />
                            <NavProvider
                                environment={
                                    COLOR_ENV === ENVIRONMENTS.production
                                        ? ENVIRONMENTS.production
                                        : ENVIRONMENTS.staging
                                }
                            >
                                <NavContainer
                                    menuItems={menuItems}
                                    countryCode={countryCode}
                                    useGrid
                                    region={region}
                                />
                            </NavProvider>
                            <PageContext.Provider value={pageContextValue}>
                                <GridLayout>
                                    <section
                                        data-zonekind={ZoneType.Full}
                                        data-component="zone"
                                    >
                                        <section data-column={1}>
                                            <TableOfContentsTitle
                                                date={displayDate}
                                            />
                                        </section>
                                    </section>
                                    <MagazineLede
                                        magazineIssues={magazineIssue}
                                        pageId={pageId}
                                        isLastInColumn
                                    />
                                    <section
                                        data-zonekind={ZoneType.Righty}
                                        data-component="zone"
                                    >
                                        <section
                                            key="magazine-column-1"
                                            className={
                                                gridStyles[
                                                    gridZonesWithColumns
                                                        .righty[0]
                                                ]
                                            }
                                            data-column={1}
                                        >
                                            <MagazinePageMagazineArchive
                                                sections={sectionModules}
                                                id={`toc-archive-${brand}`}
                                                componentName="MagazinePageMagazineArchive"
                                                renderingLocation={
                                                    ZoneColumnCombo.leftContentWell
                                                }
                                                items={[]}
                                                modules={[]}
                                                pageId={pageId}
                                                source={ModuleSource.Canonical}
                                                type="magazine_page"
                                                variation="magazine_archive"
                                                zoneName="magazine index page"
                                                isLastInColumn
                                            />
                                        </section>
                                        <section
                                            key="magazine-column-2"
                                            className={
                                                gridStyles[
                                                    gridZonesWithColumns
                                                        .righty[1]
                                                ]
                                            }
                                            data-column={2}
                                        />
                                    </section>
                                </GridLayout>
                            </PageContext.Provider>
                            <Footer footerMenuItems={footerMenuItems} />
                        </ContainerWithParallaxLeaderboard>
                    </AdOptionsProvider>
                </WinSparkleProvider>
            </TrackingProvider>
        </div>
    );
}
